// SandboxTrainListTable.tsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  TableBody,
  TableCell,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Grid,
} from '@mui/material';
import useDataStreamsForSandBox from './hooks/useDataStreamsForSandBox.ts';
import { Datastream } from 'pages/sandbox/types/datastream';
import { HistoricalData } from 'pages/sandbox/types/historicalData';
import { StatusTableProps } from 'pages/sandbox/types/statusTableProps';
import SandboxTrainDetail from './SandboxTrainDetail';
import SandboxCard from './SandBoxCard.tsx';
import SandUsageSubdividedTrackMap from "./SandUsageMap.tsx"
import SandUsageMap from './SandUsageMap';
const statusColors = {
  'ACTION REQUIRED': 'error',
  GOOD: 'success',
  MONITOR: 'warning',
};

const baseColors = ['#FF5733', '#33FF57', '#3357FF', '#FF33A1', '#A133FF', '#33FFF5'];

const getStatusColor = (level: number, profile: any): string => {
  const range = profile?.rules.ranges.find(r =>
    level >= parseInt(r.from, 10) && level <= parseInt(r.to, 10)
  );
  if (!range) return '#f44336';
  switch (range.status) {
    case 'ok': return '#4caf50';
    case 'warning': return '#ffc107';
    case 'danger': return '#f44336';
    default: return '#f44336';
  }
};

const getStatus = (level: number, profile: any): string => {
  const range = profile?.rules.ranges.find(r =>
    level >= parseInt(r.from, 10) && level <= parseInt(r.to, 10)
  );
  if (!range) return 'ACTION REQUIRED';
  switch (range.status) {
    case 'ok': return 'GOOD';
    case 'warning': return 'MONITOR';
    case 'danger': return 'ACTION REQUIRED';
    default: return 'ACTION REQUIRED';
  }
};

const MemoizedSandboxCard = React.memo(SandboxCard);
const MemoizedSandboxTrainDetail = React.memo(SandboxTrainDetail);
const MemorizedMap = React.memo(SandUsageMap)
const generatingSandBoxStatus = (datastreams: Datastream[]): any => {
  const profile = datastreams[0]?.datastream.profile;
  return {
    sandBoxStatusColors: datastreams.map((d, i) => {
      const value = parseInt(d?.datastream.latest_reading.value, 10);
      d.color = baseColors[i % baseColors.length]; // Assign unique color
      return getStatusColor(value, profile);
    }),
    sandBoxStatus: getStatus(
      Math.min(...datastreams.map(d => parseInt(d?.datastream.latest_reading.value, 10))),
      profile
    ),
    coloredDatastreams: datastreams
  };
};

const createHistoricalData = (datastreamValue: Datastream): HistoricalData[] => {
  const datastream = datastreamValue?.datastream;
  const color = datastreamValue.color;
  return [
    {
      date: 'Previous Month',
      level: datastream.last_30_days_previous,
      sandbox: datastream.labels.position,
      color
    },
    {
      date: 'Month',
      level: datastream.last_30_days,
      sandbox: datastream.labels.position,
      color
    },
    {
      date: 'Last Week',
      level: datastream.last_7_days,
      sandbox: datastream.labels.position,
      color
    },
  ];
};

const combineSandboxData = (datastreams: Datastream[]): HistoricalData[] => {
  return datastreams?.flatMap(datastream => createHistoricalData(datastream));
};

const SandboxTrainListTable: React.FC<StatusTableProps> = ({
  onSelectTrain,
  selectedTrain,
  trainsData,
  selectedSandBoxesTrain,
  selectedTrainID,
  setSelectedTrainID,
  usageTrainID,
  setUsageTrainID,
  onMapShow,
  onUpdateTrainStatus, 
  setOpenMap, 
  openMap
}) => {
  const { data, isLoading, lastFetched } = useDataStreamsForSandBox(trainsData.sandboxes);
  const [combinedHistoricalData, setCombinedHistoricalData] = useState<HistoricalData[]>([]);
  const [sandBoxStatus, setSandBoxStatus] = useState<any | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const memoizedSandBoxStatus = useMemo(() => {
    if (!isLoading && data) {
      const newStatus = generatingSandBoxStatus(data);
      if (onUpdateTrainStatus && sandBoxStatus?.sandBoxStatus !== newStatus.sandBoxStatus) {
        onUpdateTrainStatus(trainsData.trainId, newStatus.sandBoxStatus);
      }
      return newStatus;
    }
    return null;
  }, [isLoading, data, sandBoxStatus, trainsData.trainId, onUpdateTrainStatus]);

  useEffect(() => {
    if (memoizedSandBoxStatus) {
      setSandBoxStatus(memoizedSandBoxStatus);
    }
  }, [memoizedSandBoxStatus]);

  useEffect(() => {
    if (!isLoading && sandBoxStatus?.coloredDatastreams) {
      setCombinedHistoricalData(combineSandboxData(sandBoxStatus.coloredDatastreams));
    }
  }, [isLoading, sandBoxStatus]);

  const handleRowClick = (trainId: number) => {
    setExpandedRowId(prev => (prev === trainId ? null : trainId));
  };
 const isTrainUsageIsSelected = usageTrainID == trainsData.trainId
  return (
    <>
      <TableBody>
        <TableRow>
          <TableCell>
            {isLoading ? <CircularProgress /> : sandBoxStatus && (
              <Chip label={sandBoxStatus.sandBoxStatus} color={statusColors[sandBoxStatus.sandBoxStatus]} />
            )}
          </TableCell>
          <TableCell>
            {isLoading ? <CircularProgress /> : sandBoxStatus && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {sandBoxStatus.sandBoxStatusColors.map((color, idx) => (
                  <Chip
                    key={idx}
                    sx={{ backgroundColor: color, margin: '0 2px', width: '20px', height: '20px' }}
                  />
                ))}
              </div>
            )}
          </TableCell>
          <TableCell>{lastFetched}</TableCell>
          <TableCell>{trainsData.name}</TableCell>
          <TableCell>{trainsData.fleet}</TableCell>
          <TableCell>{trainsData.trainId}</TableCell>
          <TableCell>{trainsData.travellingFrom}</TableCell>
          <TableCell>{trainsData.travellingTo}</TableCell>
          <TableCell>{trainsData.headSandboxPairAvg}</TableCell>
          <TableCell>{trainsData.tailSandboxPairAvg}</TableCell>
          <TableCell>{trainsData.currentPosition}</TableCell>
          <TableCell>
          <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handleRowClick(trainsData.trainId)}
              sx={{ mr: 1 }}
            >
              {expandedRowId === trainsData.trainId ? 'Less' : 'More'}
            </Button>
            <Button
              variant={isTrainUsageIsSelected ? "contained" : "outlined"}
              color="secondary"
              size="small"
              onClick={() => {
                onMapShow();
                setOpenMap(!openMap);
                handleRowClick(trainsData.trainId)
              }}
            >
              {openMap ? 'Hide Usage' : 'Usage'}
            </Button>
          </TableCell>
        </TableRow>

        {expandedRowId === trainsData.trainId  && (
          <TableRow>
            <TableCell colSpan={12}>
            {(
        <>
        {openMap ?(<Grid container spacing={2} sx={{ mb: '1rem' }}>
            <MemorizedMap key={trainsData.trainId} />
          </Grid>) :
          
          
             <Grid container spacing={2} sx={{ mb: '1rem' }}>
                {sandBoxStatus?.coloredDatastreams?.map((datastream, index) => (
                  <Grid item xs={12} sm={6} md={1} lg={1.5} key={index}>
                    <MemoizedSandboxCard
                      name={datastream?.datastream.labels.position || `Sandbox ${index + 1}`}
                      datastreamId={datastream?.datastream.id}
                      currentLevel={parseInt(datastream?.datastream.latest_reading.value, 10)}
                      Datastream={datastream}
                      color={datastream?.color}
                      colorArray={baseColors[index % baseColors.length]}
                    />
                  </Grid>
                ))}
                <Grid item xs={12} sm={6} md={8} lg={6}>
                  <MemoizedSandboxTrainDetail
                    sandboxDetail  = {sandBoxStatus?.coloredDatastreams}
                    // sandBoxHistoricData={combinedHistoricalData}
                  />

                  
                </Grid>
              
              </Grid> 
          
          }
          
        </>
      )}
            
              
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </>
  );
};

export default SandboxTrainListTable;
